import {
  FlexBox,
  Icon,
  IconNames,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import {
  cloneElement,
  ComponentProps,
  type ReactElement,
  type ReactNode,
} from 'react';

import styles from './contactItem.module.scss';

export type ContactItemProps = {
  /** Icon to associate with the label */
  icon: IconNames;
  /** The label to associate with the address */
  label: string;
  /** Additional information shown next to the label, e.g. a Badge. */
  labelMeta?: ReactNode;
  /** The value, which can either be a simple string or a custom ReactElement. */
  value: ReactElement | string;
  /** The optional tooltip content to show when hovering the value. */
  valueTooltip?: string;
  /** The optional actions shown next to the value. */
  actions?: ReactNode;
  /** Custom props to pass to the wrapper FlexBox component. */
  flexBoxProps?: ComponentProps<typeof FlexBox>;
};

export const ContactItem = ({
  icon,
  label,
  labelMeta,
  value,
  valueTooltip,
  actions,
  flexBoxProps,
}: ContactItemProps): JSX.Element => {
  return (
    <FlexBox as="li" gap="300" {...flexBoxProps}>
      <FlexBox alignItems="center" gap="200" className={styles.labelSlot}>
        <Icon name={icon} />

        <Text.Paragraph variant="1" subdued wrap="nowrap">
          {label}
        </Text.Paragraph>

        {labelMeta}
      </FlexBox>

      <FlexBox alignItems="center" gap="200" className={styles.valueSlot}>
        <Tooltip content={valueTooltip} disabled={!valueTooltip} wrap="none">
          {typeof value === 'string' ? (
            <span className={styles.value}>{value}</span>
          ) : (
            cloneElement(value, { className: styles.value })
          )}
        </Tooltip>

        <div className={styles.actions}>{actions}</div>
      </FlexBox>
    </FlexBox>
  );
};
